import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Empty, Spin, Alert } from "antd";
import { getGoogleMapsMapId, loadGoogleMaps } from "../googleMapsLoader";

type Stop = {
    key: string;
    title: string;
    address: string;
    notes: string;
    lat?: number;
    lng?: number;
    geocodedAddress?: string;
};
type Accommodation = {
    title: string;
    address: string;
    url: string;
    notes: string;
    lat?: number;
    lng?: number;
    geocodedAddress?: string;
};
type Day = {
    itineraryIdentifier: string;
    title: string;
    notes: string;
    accommodation: Accommodation;
    stops: Stop[];
};

type Props = {
    open: boolean;
    onClose: () => void;
    days: Day[];
    tourName?: string;
};

const DAY_COLORS = [
    "#0f4c5c",
    "#e09f3e",
    "#2f7a5a",
    "#c1463b",
    "#5a4fcf",
    "#1f6f8b",
    "#a3502c",
    "#7a3b6e",
];

const dayColor = (idx: number) => DAY_COLORS[idx % DAY_COLORS.length];

// DOM content for an advanced marker pin — a coloured circle with the
// day/stop label, matching the old SymbolPath.CIRCLE markers. The
// .pa-map-pin class re-centers the circle on the coordinate (advanced
// markers anchor content bottom-center).
const makePinEl = (color: string, text: string, kind: "stop" | "accommodation") => {
    const el = document.createElement("div");
    el.className = `pa-map-pin${kind === "accommodation" ? " pa-map-pin--stay" : ""}`;
    el.style.background = color;
    el.textContent = text;
    return el;
};

const CACHE_PREFIX = "pa_geocode_v1:";

type LatLng = { lat: number; lng: number };

const readCache = (address: string): LatLng | null => {
    try {
        const raw = sessionStorage.getItem(CACHE_PREFIX + address);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (typeof parsed?.lat === "number" && typeof parsed?.lng === "number") {
            return { lat: parsed.lat, lng: parsed.lng };
        }
    } catch {
        // sessionStorage unavailable or parse failure — fall through to network geocode.
    }
    return null;
};

const writeCache = (address: string, coords: LatLng) => {
    try {
        sessionStorage.setItem(CACHE_PREFIX + address, JSON.stringify(coords));
    } catch {
        // sessionStorage write failure (quota, private mode) — non-fatal.
    }
};

const storedCoords = (record: {
    lat?: number;
    lng?: number;
    geocodedAddress?: string;
    address?: string;
}): LatLng | null => {
    if (
        typeof record.lat === "number" &&
        typeof record.lng === "number" &&
        typeof record.geocodedAddress === "string" &&
        record.geocodedAddress === (record.address || "")
    ) {
        return { lat: record.lat, lng: record.lng };
    }
    return null;
};

type GeocodeResult = { coords: LatLng | null; status: string };

const geocodeAddress = (geocoder: any, address: string): Promise<GeocodeResult> => {
    const cached = readCache(address);
    if (cached) return Promise.resolve({ coords: cached, status: "CACHED" });
    return new Promise((resolve) => {
        try {
            geocoder.geocode({ address }, (results: any, status: any) => {
                if (status === "OK" && results?.[0]?.geometry?.location) {
                    const loc = results[0].geometry.location;
                    const coords = { lat: loc.lat(), lng: loc.lng() };
                    writeCache(address, coords);
                    resolve({ coords, status: "OK" });
                } else {
                    console.warn(`[ItineraryMap] geocode failed for "${address}": ${status}`);
                    resolve({ coords: null, status: String(status || "UNKNOWN") });
                }
            });
        } catch (e) {
            console.warn(`[ItineraryMap] geocode threw for "${address}":`, e);
            resolve({ coords: null, status: "EXCEPTION" });
        }
    });
};

const escapeHtml = (s: string): string =>
    String(s || "").replace(/[&<>"']/g, (c) => {
        const map: Record<string, string> = {
            "&": "&amp;",
            "<": "&lt;",
            ">": "&gt;",
            '"': "&quot;",
            "'": "&#39;",
        };
        return map[c];
    });

type Pin = {
    coords: LatLng;
    dayIndex: number;
    label: string;
    title: string;
    address: string;
    notes: string;
    kind: "stop" | "accommodation";
    stopNumber?: number;
    isDuplicate?: boolean;
    sharedDays?: number[];
};

const ItineraryMap = ({ open, onClose, days, tourName }: Props) => {
    const [container, setContainer] = useState<HTMLDivElement | null>(null);
    const setContainerRef = useCallback((node: HTMLDivElement | null) => {
        setContainer(node);
    }, []);
    const mapRef = useRef<any>(null);
    const markersRef = useRef<any[]>([]);
    const polylinesRef = useRef<any[]>([]);
    const infoRef = useRef<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [skippedCount, setSkippedCount] = useState(0);
    // Deep-link to Google Maps directions through the whole located tour,
    // set once the pins are geocoded (null while there's nothing to route).
    const [rideDirectionsUrl, setRideDirectionsUrl] = useState<string | null>(null);

    const totalAddresses = useMemo(() => {
        let n = 0;
        for (const d of days) {
            n += (d.stops || []).filter((s) => (s.address || "").trim()).length;
            if ((d.accommodation?.address || "").trim()) n += 1;
        }
        return n;
    }, [days]);

    useEffect(() => {
        if (!open) return;
        if (totalAddresses === 0) return;
        if (!container) return;

        let cancelled = false;
        setLoading(true);
        setError(null);
        setSkippedCount(0);
        setRideDirectionsUrl(null);

        (async () => {
            const google = await loadGoogleMaps();
            if (cancelled) return;
            if (!google?.maps) {
                setError("Google Maps failed to load. Check your network or API key.");
                setLoading(false);
                return;
            }

            const map = new google.maps.Map(container, {
                center: { lat: 48.8566, lng: 2.3522 },
                zoom: 5,
                // Required for Advanced Markers.
                mapId: getGoogleMapsMapId(),
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: true,
            });
            mapRef.current = map;
            infoRef.current = new google.maps.InfoWindow();

            const geocoder = new google.maps.Geocoder();
            const pins: Pin[] = [];
            let skipped = 0;
            const failureStatuses = new Set<string>();

            const accommodationDayMap = new Map<string, number[]>();
            for (let d = 0; d < days.length; d++) {
                const accAddr = (days[d].accommodation?.address || "").trim();
                if (!accAddr) continue;
                const existing = accommodationDayMap.get(accAddr);
                if (existing) existing.push(d);
                else accommodationDayMap.set(accAddr, [d]);
            }

            for (let d = 0; d < days.length; d++) {
                const day = days[d];
                const stops = day.stops || [];
                let stopOrdinal = 0;
                for (let s = 0; s < stops.length; s++) {
                    const stop = stops[s];
                    const addr = (stop.address || "").trim();
                    if (!addr) continue;
                    stopOrdinal += 1;
                    let coords: LatLng | null = storedCoords(stop);
                    if (!coords) {
                        const result = await geocodeAddress(geocoder, addr);
                        if (cancelled) return;
                        coords = result.coords;
                        if (!coords) {
                            skipped++;
                            failureStatuses.add(result.status);
                            continue;
                        }
                    }
                    pins.push({
                        coords,
                        dayIndex: d,
                        label: `${d + 1}.${stopOrdinal}`,
                        title: stop.title || addr,
                        address: addr,
                        notes: stop.notes || "",
                        kind: "stop",
                        stopNumber: stopOrdinal,
                    });
                }
                const accAddr = (day.accommodation?.address || "").trim();
                if (accAddr) {
                    let coords: LatLng | null = storedCoords({ ...day.accommodation, address: accAddr });
                    let failStatus = "";
                    if (!coords) {
                        const result = await geocodeAddress(geocoder, accAddr);
                        if (cancelled) return;
                        coords = result.coords;
                        failStatus = result.status;
                    }
                    if (coords) {
                        const sharedDays = accommodationDayMap.get(accAddr) || [d];
                        const isShared = sharedDays.length > 1;
                        const isFirstOccurrence = sharedDays[0] === d;
                        pins.push({
                            coords,
                            dayIndex: d,
                            label: isShared ? "*H" : `${d + 1}H`,
                            title: day.accommodation?.title || "Accommodation",
                            address: accAddr,
                            notes: day.accommodation?.notes || "",
                            kind: "accommodation",
                            isDuplicate: isShared && !isFirstOccurrence,
                            sharedDays: isShared ? sharedDays : undefined,
                        });
                    } else {
                        skipped++;
                        if (failStatus) failureStatuses.add(failStatus);
                    }
                }
            }

            if (pins.length === 0 && skipped > 0) {
                if (failureStatuses.has("REQUEST_DENIED")) {
                    setError(
                        "Geocoding API is not enabled or the API key is restricted. Enable the Geocoding API for this project in Google Cloud Console.",
                    );
                } else if (failureStatuses.has("OVER_QUERY_LIMIT")) {
                    setError(
                        "Geocoding quota exceeded for this API key. Check billing and quota in Google Cloud Console.",
                    );
                } else if (failureStatuses.has("ZERO_RESULTS")) {
                    setError(
                        "None of the addresses could be located. Try more specific addresses (e.g. include city/country).",
                    );
                } else {
                    setError(
                        `Geocoding failed for all addresses (${[...failureStatuses].join(", ")}). See browser console for details.`,
                    );
                }
            }

            if (cancelled) return;

            const bounds = new google.maps.LatLngBounds();
            for (const pin of pins) {
                bounds.extend(pin.coords);
                if (pin.isDuplicate) continue;
                const color = dayColor(pin.dayIndex);
                const marker = new google.maps.marker.AdvancedMarkerElement({
                    position: pin.coords,
                    map,
                    content: makePinEl(color, pin.label, pin.kind),
                    title: pin.title,
                    gmpClickable: true,
                });
                marker.addListener("click", () => {
                    const subtitle =
                        pin.kind === "accommodation"
                            ? pin.sharedDays && pin.sharedDays.length > 1
                                ? `Days ${pin.sharedDays.map((i) => i + 1).join(", ")} · Stay`
                                : `Day ${pin.dayIndex + 1} · Stay`
                            : `Day ${pin.dayIndex + 1} · Stop ${pin.stopNumber}`;
                    const content = `
                        <div style="font-family: system-ui, sans-serif; max-width: 240px;">
                            <div style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.4px; color: #66707b; margin-bottom: 4px;">${escapeHtml(subtitle)}</div>
                            <div style="font-weight: 600; font-size: 14px; margin-bottom: 4px;">${escapeHtml(pin.title)}</div>
                            <div style="font-size: 12px; color: #66707b; margin-bottom: ${pin.notes ? "6px" : "0"};">${escapeHtml(pin.address)}</div>
                            ${pin.notes ? `<div style="font-size: 12px; color: #1c2433; white-space: pre-wrap;">${escapeHtml(pin.notes)}</div>` : ""}
                        </div>
                    `;
                    infoRef.current.setContent(content);
                    infoRef.current.open({ anchor: marker, map });
                });
                markersRef.current.push(marker);
            }

            const dayPinsByDay: Pin[][] = [];
            for (let d = 0; d < days.length; d++) {
                dayPinsByDay.push(pins.filter((p) => p.dayIndex === d));
            }

            for (let d = 0; d < days.length; d++) {
                const dayPins = dayPinsByDay[d];
                if (dayPins.length < 2) continue;
                const line = new google.maps.Polyline({
                    path: dayPins.map((p) => p.coords),
                    geodesic: true,
                    strokeColor: dayColor(d),
                    strokeOpacity: 0.9,
                    strokeWeight: 3,
                    map,
                });
                polylinesRef.current.push(line);
            }

            for (let d = 0; d < days.length - 1; d++) {
                const current = dayPinsByDay[d];
                const next = dayPinsByDay[d + 1];
                if (current.length === 0 || next.length === 0) continue;
                const interDay = new google.maps.Polyline({
                    path: [current[current.length - 1].coords, next[0].coords],
                    geodesic: true,
                    strokeOpacity: 0,
                    icons: [
                        {
                            icon: {
                                path: "M 0,-1 0,1",
                                strokeOpacity: 0.7,
                                strokeColor: "#66707b",
                                scale: 3,
                            },
                            offset: "0",
                            repeat: "10px",
                        },
                    ],
                    map,
                });
                polylinesRef.current.push(interDay);
            }

            setSkippedCount(skipped);

            // Build a single directions deep-link through every located point
            // (origin → waypoints → destination). Google's dir URL takes up to
            // ~9 intermediate waypoints, so trim the middle if the tour is long.
            const routeAddresses = pins.map((p) => p.address).filter(Boolean);
            if (routeAddresses.length >= 2) {
                const origin = routeAddresses[0];
                const destination = routeAddresses[routeAddresses.length - 1];
                const middle = routeAddresses.slice(1, -1).slice(0, 9);
                let url = `https://www.google.com/maps/dir/?api=1&travelmode=driving&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}`;
                if (middle.length > 0) {
                    url += `&waypoints=${middle.map(encodeURIComponent).join("|")}`;
                }
                setRideDirectionsUrl(url);
            }

            if (pins.length > 0) {
                map.fitBounds(bounds, 60);
                if (pins.length === 1) map.setZoom(13);
            }
            setLoading(false);
        })();

        return () => {
            cancelled = true;
            // Advanced markers detach via the map property (no setMap method
            // — an optional-chained setMap would silently no-op and leak
            // pins across re-renders).
            for (const m of markersRef.current) m.map = null;
            markersRef.current = [];
            for (const p of polylinesRef.current) p.setMap?.(null);
            polylinesRef.current = [];
            if (infoRef.current?.close) infoRef.current.close();
            mapRef.current = null;
        };
    }, [open, days, totalAddresses, container]);

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            title={tourName ? `Map · ${tourName}` : "Map"}
            width="min(1100px, 96vw)"
            destroyOnHidden
            styles={{ body: { padding: 0 } }}
        >
            {totalAddresses === 0 ? (
                <div style={{ padding: 40 }}>
                    <Empty description="Add stop or accommodation addresses to see them on the map." />
                </div>
            ) : (
                <>
                <div style={{ position: "relative" }}>
                    <div
                        ref={setContainerRef}
                        style={{
                            width: "100%",
                            height: "min(72vh, 640px)",
                            background: "var(--gt-bg)",
                        }}
                    />
                    {loading && (
                        <div
                            style={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "rgba(255,255,255,0.6)",
                                pointerEvents: "none",
                            }}
                        >
                            <Spin description="Locating addresses…" />
                        </div>
                    )}
                    {error && (
                        <div style={{ padding: 16 }}>
                            <Alert type="error" title={error} showIcon />
                        </div>
                    )}
                    {!loading && !error && skippedCount > 0 && (
                        <div
                            style={{
                                padding: "8px 16px",
                                background: "#fffaf0",
                                fontSize: 12,
                                color: "#66707b",
                            }}
                        >
                            {skippedCount} address{skippedCount === 1 ? "" : "es"} could not be located and {skippedCount === 1 ? "is" : "are"} not shown.
                        </div>
                    )}
                </div>
                {!error && (
                    <div
                        style={{
                            background: "var(--gt-surface)",
                            borderTop: "1px solid var(--gt-border)",
                            padding: "12px 14px",
                        }}
                    >
                        <a
                            href={rideDirectionsUrl || undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-disabled={!rideDirectionsUrl}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 8,
                                background: "var(--gt-primary)",
                                color: "#fff",
                                borderRadius: 11,
                                padding: 14,
                                fontSize: 15,
                                fontWeight: 600,
                                textDecoration: "none",
                                opacity: rideDirectionsUrl ? 1 : 0.5,
                                pointerEvents: rideDirectionsUrl ? "auto" : "none",
                            }}
                        >
                            🏍 Get directions for the whole tour
                        </a>
                    </div>
                )}
                </>
            )}
        </Modal>
    );
};

export default ItineraryMap;
